import { Badge, Button, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ListIcon } from 'lucide-react';
import { useListsQuery } from '../../../api/queries.ts';
import { NotWired } from '../../../components/not-wired.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';

export const Route = createFileRoute('/_authed/lists/')({
  component: ListsPage,
  errorComponent: RouteErrorPanel,
});

function ListsPage() {
  const lists = useListsQuery();

  return (
    <>
      <PageHeader title="Lists" description="Static and smart groupings of contacts." />
      <PageBody>
        {lists.isLoading ? (
          <SkeletonRows rows={5} />
        ) : lists.isError ? (
          <EmptyState kind="error" title="Could not load lists" />
        ) : (lists.data?.items ?? []).length === 0 ? (
          <EmptyState
            icon={<ListIcon aria-hidden />}
            title="No lists yet"
            description="Lists are how you group people you want to treat the same way — a fund's portfolio, a dinner's guests, a hiring pipeline."
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {(lists.data?.items ?? []).map((list) => (
              <li key={list.id}>
                <Link
                  to="/lists/$listId"
                  params={{ listId: list.id }}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-sm hover:bg-accent/50"
                >
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{list.name}</span>
                    <Badge variant="muted">{list.kind}</Badge>
                    {list.shareCount > 0 ? (
                      <Badge variant="secondary">shared ×{list.shareCount}</Badge>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground">{list.itemCount} contacts</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <NotWired
          title="Create, rename, share"
          blockedOn="Procedures exist (lists.create / lists.update / shares.list.create) — the UI for them is not built in this track"
        >
          <Button size="sm" variant="outline" disabled>
            New list
          </Button>
        </NotWired>
      </PageBody>
    </>
  );
}
